import React, { useEffect, useMemo, useState } from "react";
import { Expandable } from "@/components/Expandable";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    Briefcase,
    Plus,
    LayoutGrid,
    Table as TableIcon,
    Search,
    Users,
    Crown,
    Trash2,
    Pencil,
    X,
    Save,
    Check,
    Loader2,
    Link as LinkIcon,
    ExternalLink,
    Plus as PlusIcon,
    Minus,
    Upload,
    Image as ImageIcon,
    ArrowUpDown,
    ArrowUp,
    ArrowDown,
} from "lucide-react";

// Cover-crop a File into a square PNG data URL at `size × size`. Used by the
// project icon uploader so payloads stay bounded (typical output ≈ 40-80 KB).
const fileToSquareDataUrl = (file, size = 256, quality = 0.9) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("read failed"));
    reader.onload = (e) => {
        const img = new Image();
        img.onerror = () => reject(new Error("image decode failed"));
        img.onload = () => {
            const min = Math.min(img.width, img.height);
            const sx = (img.width - min) / 2;
            const sy = (img.height - min) / 2;
            const canvas = document.createElement("canvas");
            canvas.width = size;
            canvas.height = size;
            const ctx = canvas.getContext("2d");
            ctx.imageSmoothingQuality = "high";
            ctx.drawImage(img, sx, sy, min, min, 0, 0, size, size);
            // JPEG unless the source is a PNG with alpha — most icons don't
            // need transparency and JPEG halves the byte count.
            const useJpeg = !/png|webp/i.test(file.type || "");
            resolve(canvas.toDataURL(useJpeg ? "image/jpeg" : "image/png", quality));
        };
        img.src = e.target.result;
    };
    reader.readAsDataURL(file);
});

const STATUSES = [
    { id: "planning", label: "Planning", cls: "bg-slate-100 text-slate-700 border-slate-200", dot: "bg-slate-400" },
    { id: "active", label: "Active", cls: "bg-teal-50 text-[#1F7F79] border-teal-200", dot: "bg-[#2EB4AC]" },
    { id: "on_hold", label: "On Hold", cls: "bg-amber-50 text-amber-800 border-amber-200", dot: "bg-amber-400" },
    { id: "done", label: "Done", cls: "bg-emerald-50 text-emerald-800 border-emerald-200", dot: "bg-emerald-500" },
    { id: "cancelled", label: "Cancelled", cls: "bg-rose-50 text-rose-700 border-rose-200", dot: "bg-rose-400" },
];
const statusMeta = (id) => STATUSES.find((s) => s.id === id) || STATUSES[1];

const initialsOf = (n) => (n || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("");

// Compact square tile that renders a project's icon (if any) or a fallback
// pattern derived from the project name. Used on cards, table rows, and
// popups so an icon-less project still looks intentional.
const ProjectIcon = ({ project, size = 44, className = "" }) => {
    const px = `${size}px`;
    if (project?.icon_url) {
        return (
            <img
                src={project.icon_url}
                alt={`${project?.name || "project"} icon`}
                style={{ width: px, height: px }}
                className={`rounded-lg object-cover ring-1 ring-slate-200 shrink-0 ${className}`}
                data-testid={`project-icon-${project?.id}`}
            />
        );
    }
    // Deterministic fallback: seed a gradient from the project name so
    // icon-less projects each get a stable, unique-looking tile instead of
    // the generic grey square everyone else uses.
    const seed = (project?.name || "?").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
    const hues = [188, 174, 210, 262, 20, 335, 148];
    const h = hues[seed % hues.length];
    return (
        <div
            style={{
                width: px,
                height: px,
                background: `linear-gradient(135deg, hsl(${h} 68% 52%) 0%, hsl(${(h + 40) % 360} 68% 40%) 100%)`,
            }}
            className={`rounded-lg text-white font-heading font-bold flex items-center justify-center ring-1 ring-black/5 shrink-0 ${className}`}
            data-testid={`project-icon-${project?.id || "none"}`}
        >
            <span style={{ fontSize: size * 0.36 }}>{initialsOf(project?.name)}</span>
        </div>
    );
};

// Square drag-and-drop / click-to-upload tile used inside the ProjectDialog.
const IconPicker = ({ iconUrl, busy, onPick, onClear }) => {
    const inputRef = React.useRef(null);
    return (
        <div className="shrink-0">
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Icon</label>
            <div className="mt-1.5 flex items-start gap-2">
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    disabled={busy}
                    data-testid="project-icon-upload-btn"
                    className="relative h-[72px] w-[72px] rounded-xl border-2 border-dashed border-slate-200 hover:border-[#2EB4AC] hover:bg-teal-50/40 focus:outline-none focus:ring-2 focus:ring-[#2EB4AC]/30 transition flex items-center justify-center overflow-hidden group"
                    aria-label={iconUrl ? "Replace icon" : "Upload icon"}
                >
                    {iconUrl ? (
                        <>
                            <img src={iconUrl} alt="icon preview" className="h-full w-full object-cover" />
                            <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/40 transition flex items-center justify-center opacity-0 group-hover:opacity-100">
                                <Upload className="h-5 w-5 text-white" />
                            </div>
                        </>
                    ) : busy ? (
                        <Loader2 className="h-5 w-5 text-[#2EB4AC] animate-spin" />
                    ) : (
                        <div className="flex flex-col items-center gap-1 text-slate-400">
                            <ImageIcon className="h-5 w-5" />
                            <span className="text-[10px] font-semibold">Upload</span>
                        </div>
                    )}
                </button>
                {iconUrl && (
                    <button
                        type="button"
                        onClick={onClear}
                        data-testid="project-icon-clear-btn"
                        className="text-slate-400 hover:text-rose-500 rounded-md p-1"
                        aria-label="Remove icon"
                    >
                        <X className="h-4 w-4" />
                    </button>
                )}
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(e) => onPick?.(e.target.files?.[0])}
                    className="hidden"
                    data-testid="project-icon-file-input"
                />
            </div>
            <p className="text-[10px] text-slate-400 mt-1">Square, PNG/JPG</p>
        </div>
    );
};

const canEditProject = (p, currentUser) => {
    if (!p || !currentUser) return false;
    if (currentUser.role === "super_admin") return true;
    const uid = currentUser.user_id;
    if (p.lead_id === uid) return true;
    if ((p.member_ids || []).includes(uid)) return true;
    if (p.created_by === uid) return true;
    return false;
};

const canDeleteProject = (p, currentUser) => {
    // Stricter than edit — only lead, creator or admin. Members alone
    // cannot delete a shared project even though they can edit its metadata.
    if (!p || !currentUser) return false;
    if (currentUser.role === "super_admin") return true;
    const uid = currentUser.user_id;
    return p.lead_id === uid || p.created_by === uid;
};

const Projects = () => {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [directory, setDirectory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState("grid"); // grid | table | people
    const [personFilter, setPersonFilter] = useState(null); // { user_id, name }
    const [scope, setScope] = useState("mine"); // mine | all
    const [q, setQ] = useState("");
    const [dialog, setDialog] = useState(null); // {mode:"create"|"edit", project?}
    const [details, setDetails] = useState(null); // project object for the details popup

    const load = async () => {
        try {
            setLoading(true);
            const r = await api.get("/projects", { params: { mine: scope === "mine" } });
            setItems(r.data?.items || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load projects");
        } finally {
            setLoading(false);
        }
    };

    // Directory = full company roster (see backend /api/directory). Loaded
    // once so cross-functional pickers can add anyone — the useData()
    // `users` list is scoped to the caller's reporting subtree, which is
    // the right default for admin surfaces but wrong for a project member
    // picker where you might collab with someone in another department.
    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/directory");
                setDirectory(Array.isArray(r.data) ? r.data : []);
            } catch { setDirectory([]); }
        })();
    }, []);

    useEffect(() => { load(); /* eslint-disable-line */ }, [scope]);

    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        const base = personFilter ? items.filter((p) => isOnProject(p, personFilter.user_id)) : items;
        if (!s) return base;
        return base.filter((p) =>
            (p.name || "").toLowerCase().includes(s) ||
            (p.description || "").toLowerCase().includes(s) ||
            (p.lead?.name || "").toLowerCase().includes(s) ||
            (p.members || []).some((m) => (m.name || "").toLowerCase().includes(s))
        );
    }, [items, q, personFilter]);

    const pickView = (id) => {
        setView(id);
        if (id === "people" && scope !== "all") setScope("all");
    };
    const pickPerson = (u) => {
        setPersonFilter({ user_id: u.user_id, name: u.name });
        setQ("");
        setView("table");
    };

    return (
        <div className="space-y-6" data-testid="projects-page">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                    <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Focus</div>
                    <h1 className="font-heading text-4xl font-bold text-slate-900 tracking-tight mt-1 flex items-center gap-3">
                        <Briefcase className="h-7 w-7 text-[#2EB4AC]" />
                        Projects
                    </h1>
                    <p className="text-slate-500 mt-1">Every initiative you and your teammates are contributing to.</p>
                </div>
                <button
                    onClick={() => setDialog({ mode: "create" })}
                    data-testid="projects-new-btn"
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#2EB4AC] hover:bg-[#25908A] text-white text-sm font-semibold shadow-sm transition-colors"
                >
                    <Plus className="h-4 w-4" /> New project
                </button>
            </div>

            {/* Controls */}
            <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                <div className="inline-flex bg-white border border-slate-200 rounded-lg p-1 shadow-sm">
                    <button
                        onClick={() => setScope("mine")}
                        data-testid="projects-scope-mine"
                        className={`h-8 px-3 text-xs font-semibold rounded-md transition-colors ${scope === "mine" ? "bg-[#2EB4AC] text-white" : "text-slate-600 hover:bg-slate-50"}`}
                    >
                        My projects
                    </button>
                    <button
                        onClick={() => setScope("all")}
                        data-testid="projects-scope-all"
                        className={`h-8 px-3 text-xs font-semibold rounded-md transition-colors ${scope === "all" ? "bg-[#2EB4AC] text-white" : "text-slate-600 hover:bg-slate-50"}`}
                    >
                        All projects
                    </button>
                </div>

                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder={view === "people" ? "Search people…" : "Search by name, lead, member…"}
                        data-testid="projects-search"
                        className="w-full h-10 pl-9 pr-3 text-sm bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#2EB4AC]/25 focus:border-[#2EB4AC] outline-none"
                    />
                </div>

                <div className="inline-flex bg-white border border-slate-200 rounded-lg p-1 shadow-sm lg:ml-auto" data-testid="projects-view-toggle">
                    {[
                        { id: "grid", icon: LayoutGrid, label: "Grid" },
                        { id: "table", icon: TableIcon, label: "Table" },
                        { id: "people", icon: Users, label: "People" },
                    ].map((v) => (
                        <button
                            key={v.id}
                            onClick={() => pickView(v.id)}
                            data-testid={`projects-view-${v.id}`}
                            className={`inline-flex items-center gap-1.5 h-8 px-2.5 text-xs font-semibold rounded-md transition-colors ${
                                view === v.id ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
                            }`}
                        >
                            <v.icon className="h-3.5 w-3.5" />
                            <span className="hidden sm:inline">{v.label}</span>
                        </button>
                    ))}
                </div>
            </div>

            {personFilter && view !== "people" && (
                <div className="flex items-center gap-2 text-sm" data-testid="projects-person-filter">
                    <span className="text-slate-500">Showing projects for</span>
                    <span className="inline-flex items-center gap-1.5 h-7 pl-2.5 pr-1 rounded-full bg-teal-50 border border-teal-200 text-[#25908A] font-semibold">
                        {personFilter.name}
                        <button
                            onClick={() => setPersonFilter(null)}
                            data-testid="projects-person-filter-clear"
                            className="h-5 w-5 rounded-full hover:bg-teal-100 inline-flex items-center justify-center"
                            aria-label="Clear person filter"
                        >
                            <X className="h-3 w-3" />
                        </button>
                    </span>
                    <span className="text-slate-400">· {filtered.length} project{filtered.length === 1 ? "" : "s"}</span>
                </div>
            )}

            {view === "people" && !loading ? (
                <PeopleView projects={items} directory={directory} q={q} onPick={pickPerson} onOpen={setDetails} />
            ) : loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[0,1,2].map((i) => <div key={i} className="h-40 bg-slate-100 rounded-lg animate-pulse" />)}
                </div>
            ) : filtered.length === 0 ? (
                <EmptyState onCreate={() => setDialog({ mode: "create" })} scope={scope} />
            ) : view === "grid" ? (
                <GridView projects={filtered} currentUser={user} onOpen={setDetails} onEdit={(p) => setDialog({ mode: "edit", project: p })} />
            ) : (
                <TableView projects={filtered} currentUser={user} onOpen={setDetails} onEdit={(p) => setDialog({ mode: "edit", project: p })} />
            )}

            {details && (
                <ProjectDetailsPopup
                    project={details}
                    currentUser={user}
                    onClose={() => setDetails(null)}
                    onEdit={() => { setDialog({ mode: "edit", project: details }); setDetails(null); }}
                />
            )}

            {dialog && (
                <ProjectDialog
                    project={dialog.project}
                    users={directory}
                    onClose={() => setDialog(null)}
                    onSaved={async () => {
                        setDialog(null);
                        await load();
                    }}
                    onDeleted={async () => {
                        setDialog(null);
                        await load();
                    }}
                    currentUser={user}
                />
            )}
        </div>
    );
};

const EmptyState = ({ onCreate, scope }) => (
    <div className="border-2 border-dashed border-teal-300 bg-teal-50/30 rounded-lg p-10 text-center">
        <Briefcase className="h-8 w-8 text-[#2EB4AC] mx-auto" />
        <p className="mt-3 font-heading font-semibold text-slate-900">
            {scope === "mine" ? "You're not on any projects yet" : "No projects yet"}
        </p>
        <p className="text-slate-500 text-sm mt-1 mb-4">
            {scope === "mine" ? "Kick off a new one or ask a teammate to add you." : "Kick off the first initiative — it takes a minute."}
        </p>
        <button
            onClick={onCreate}
            data-testid="projects-empty-cta"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#2EB4AC] hover:bg-[#25908A] text-white text-sm font-semibold"
        >
            <Plus className="h-4 w-4" /> New project
        </button>
    </div>
);

// ---------- Grid ----------
const ProjectCard = ({ p, currentUser, onOpen, onEdit }) => {
    const s = statusMeta(p.status);
    const canEdit = canEditProject(p, currentUser);
    const memberCount = (p.members || []).length;
    return (
        <div
            onClick={() => onOpen?.(p)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen?.(p); } }}
            className="group bg-white rounded-xl border border-slate-200 shadow-sm p-5 hover:shadow-md hover:border-[#2EB4AC]/40 transition-all flex flex-col cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#2EB4AC]/40"
            data-testid={`project-card-${p.id}`}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-3 min-w-0">
                    <ProjectIcon project={p} size={40} />
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${s.cls}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
                        {s.label}
                    </span>
                </div>
                {canEdit && (
                    <button
                        onClick={(e) => { e.stopPropagation(); onEdit(p); }}
                        data-testid={`project-edit-${p.id}`}
                        className="opacity-0 group-hover:opacity-100 transition-opacity h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-slate-100 text-slate-500"
                        aria-label="Edit"
                    >
                        <Pencil className="h-3.5 w-3.5" />
                    </button>
                )}
            </div>
            <h3 className="mt-3 font-heading text-lg font-semibold text-slate-900 line-clamp-2">{p.name}</h3>
            {p.description && <p className="text-sm text-slate-500 mt-1 line-clamp-3">{p.description}</p>}

            <div className="mt-4 flex items-center gap-2">
                <Avatar user={p.lead} ring="ring-2 ring-amber-300" />
                <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold flex items-center gap-1"><Crown className="h-3 w-3 text-amber-500" /> Lead</div>
                    <div className="text-sm font-semibold text-slate-800 truncate">{p.lead?.name || "—"}</div>
                </div>
            </div>

            <div className="mt-3 flex items-center gap-2 flex-wrap">
                <div className="flex flex-wrap -space-x-2 gap-y-1">
                    <Expandable
                        items={p.members || []}
                        limit={6}
                        moreLabel={(n) => `+${n}`}
                        className="h-7 min-w-[1.75rem] px-1 rounded-full bg-slate-100 text-slate-600 text-[10px] font-semibold flex items-center justify-center ring-2 ring-white hover:bg-teal-50 hover:text-[#25908A] no-underline"
                        testid={`project-card-members-more-${p.id}`}
                        render={(m) => <Avatar key={m.user_id} user={m} size="sm" ring="ring-2 ring-white" />}
                    />
                </div>
                <span className="text-xs text-slate-500 inline-flex items-center gap-1"><Users className="h-3 w-3" /> {memberCount}</span>
            </div>

            <LinkStrip project={p} max={4} onOverflow={() => onOpen?.(p)} />
        </div>
    );
};

const GridView = ({ projects, currentUser, onOpen, onEdit }) => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {projects.map((p) => <ProjectCard key={p.id} p={p} currentUser={currentUser} onOpen={onOpen} onEdit={onEdit} />)}
    </div>
);

// ---------- Table ----------
const TableView = ({ projects, currentUser, onOpen, onEdit }) => (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-x-auto">
        <table className="w-full" data-testid="projects-table">
            <thead className="bg-slate-50 border-b border-slate-100">
                <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <th className="px-5 py-3">Project</th>
                    <th className="px-5 py-3">Status</th>
                    <th className="px-5 py-3">Lead</th>
                    <th className="px-5 py-3">Team</th>
                    <th className="px-5 py-3"></th>
                </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
                {projects.map((p) => {
                    const s = statusMeta(p.status);
                    const canEdit = canEditProject(p, currentUser);
                    return (
                        <tr
                            key={p.id}
                            onClick={() => onOpen?.(p)}
                            className="hover:bg-slate-50 cursor-pointer"
                            data-testid={`project-row-${p.id}`}
                        >
                            <td className="px-5 py-3">
                                <div className="flex items-center gap-3">
                                    <ProjectIcon project={p} size={36} />
                                    <div className="min-w-0">
                                        <div className="font-medium text-slate-900">{p.name}</div>
                                        {p.description && <div className="text-xs text-slate-500 line-clamp-1">{p.description}</div>}
                                    </div>
                                </div>
                            </td>
                            <td className="px-5 py-3">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${s.cls}`}>
                                    <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
                                    {s.label}
                                </span>
                            </td>
                            <td className="px-5 py-3">
                                <div className="flex items-center gap-2">
                                    <Avatar user={p.lead} size="sm" ring="ring-2 ring-amber-300" />
                                    <span className="text-sm text-slate-700">{p.lead?.name || "—"}</span>
                                </div>
                            </td>
                            <td className="px-5 py-3 min-w-[280px]">
                                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5 flex items-center gap-1" data-testid={`project-member-count-${p.id}`}>
                                    <Users className="h-3 w-3" /> {projectPeople(p).length} {projectPeople(p).length === 1 ? "person" : "people"}
                                </div>
                                <MemberChips p={p} />
                            </td>
                            <td className="px-5 py-3 text-right">
                                {canEdit && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); onEdit(p); }}
                                        data-testid={`project-row-edit-${p.id}`}
                                        className="inline-flex items-center h-8 px-2.5 rounded-md text-xs font-semibold text-[#25908A] hover:bg-teal-50"
                                    >
                                        <Pencil className="h-3 w-3 mr-1" /> Edit
                                    </button>
                                )}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    </div>
);

const isOnProject = (p, uid) => p.lead_id === uid || (p.member_ids || []).includes(uid);

// Lead first (crowned), then the rest — de-duplicated.
const projectPeople = (p) => {
    const seen = new Set();
    const out = [];
    if (p.lead) { out.push({ ...p.lead, _lead: true }); seen.add(p.lead.user_id); }
    for (const m of p.members || []) if (!seen.has(m.user_id)) { out.push(m); seen.add(m.user_id); }
    return out;
};

const MemberChips = ({ p }) => {
    const people = projectPeople(p);
    if (!people.length) return <span className="text-xs text-slate-400 italic">No members yet</span>;
    return (
        <div className="flex flex-wrap gap-1.5" data-testid={`project-members-${p.id}`}>
            {people.map((m) => (
                <span
                    key={m.user_id}
                    title={`${m.name}${m._lead ? " · Lead" : ""}`}
                    className={`inline-flex items-center gap-1.5 h-7 pl-0.5 pr-2.5 rounded-full border text-xs font-medium ${m._lead ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-slate-50 border-slate-200 text-slate-700"}`}
                >
                    <Avatar user={m} size="sm" />
                    {(m.name || "?").split(" ")[0]}
                    {m._lead && <Crown className="h-3 w-3 text-amber-500" />}
                </span>
            ))}
        </div>
    );
};

// ---------- People view: who is on how many projects ----------
const PeopleView = ({ projects, directory, q, onPick, onOpen }) => {
    const [sort, setSort] = useState({ key: "count", dir: "desc" });
    const [quick, setQuick] = useState("all"); // all | with | none
    const allRows = useMemo(() => {
        const byId = new Map(directory.map((u) => [u.user_id, { ...u, projects: [], leading: 0 }]));
        for (const p of projects) {
            for (const m of projectPeople(p)) {
                if (!byId.has(m.user_id)) byId.set(m.user_id, { ...m, projects: [], leading: 0 });
                const r = byId.get(m.user_id);
                r.projects.push({ p, lead: !!m._lead });
                if (m._lead) r.leading += 1;
            }
        }
        const s = q.trim().toLowerCase();
        const list = [...byId.values()].filter((u) => !s || (u.name || "").toLowerCase().includes(s) || (u.designation || "").toLowerCase().includes(s));
        const dir = sort.dir === "asc" ? 1 : -1;
        list.sort((a, b) => {
            if (sort.key === "name") return dir * (a.name || "").localeCompare(b.name || "");
            const v = sort.key === "leading" ? a.leading - b.leading : a.projects.length - b.projects.length;
            return dir * v || (a.name || "").localeCompare(b.name || "");
        });
        return list;
    }, [projects, directory, q, sort]);
    const rows = useMemo(
        () => allRows.filter((u) => quick === "all" || (quick === "with" ? u.projects.length > 0 : u.projects.length === 0)),
        [allRows, quick],
    );

    const toggle = (key) => setSort((p) => ({ key, dir: p.key === key && p.dir === "desc" ? "asc" : key === "name" && p.key !== key ? "asc" : "desc" }));
    const Head = ({ k, label, className = "" }) => {
        const active = sort.key === k;
        const Icon = !active ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
        return (
            <th className={`px-5 py-3 cursor-pointer select-none hover:bg-slate-100 transition-colors ${className}`} onClick={() => toggle(k)} data-testid={`people-sort-${k}`}>
                <span className={`inline-flex items-center gap-1 ${active ? "text-[#25908A]" : ""}`}>{label}<Icon className="h-3 w-3" /></span>
            </th>
        );
    };
    const unassigned = allRows.filter((r) => !r.projects.length).length;
    const chips = [
        { id: "all", label: "All", n: allRows.length },
        { id: "with", label: "With projects", n: allRows.length - unassigned },
        { id: "none", label: "No projects", n: unassigned },
    ];

    return (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-x-auto" data-testid="projects-people-view">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2 flex-wrap" data-testid="people-quick-filters">
                {chips.map((c) => (
                    <button
                        key={c.id}
                        onClick={() => setQuick(c.id)}
                        data-testid={`people-filter-${c.id}`}
                        className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full border text-xs font-semibold transition-colors ${quick === c.id ? "bg-[#2EB4AC] border-[#2EB4AC] text-white" : "bg-white border-slate-200 text-slate-600 hover:border-[#2EB4AC] hover:text-[#25908A]"}`}
                    >
                        {c.label}
                        <span className={`inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full text-[10px] tabular-nums ${quick === c.id ? "bg-white/25 text-white" : c.id === "none" && c.n ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{c.n}</span>
                    </button>
                ))}
            </div>
            <table className="w-full" data-testid="people-table">
                <thead className="bg-slate-50 border-b border-slate-100">
                    <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                        <Head k="name" label="Employee" />
                        <Head k="count" label="Projects" className="text-center w-32" />
                        <Head k="leading" label="Leading" className="text-center w-28" />
                        <th className="px-5 py-3">Enrolled in</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                    {rows.map((u) => (
                        <tr key={u.user_id} onClick={() => u.projects.length && onPick(u)} className={u.projects.length ? "hover:bg-slate-50 cursor-pointer" : ""} data-testid={`people-row-${u.user_id}`}>
                            <td className="px-5 py-3">
                                <div className="flex items-center gap-2.5">
                                    <Avatar user={u} />
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium text-slate-900">{u.name}</div>
                                        {u.designation && <div className="text-xs text-slate-500 truncate">{u.designation}</div>}
                                    </div>
                                </div>
                            </td>
                            <td className="px-5 py-3 text-center">
                                <span
                                    className={`inline-flex items-center justify-center min-w-[2rem] h-7 px-2 rounded-full text-sm font-bold tabular-nums ${u.projects.length ? "bg-teal-50 text-[#25908A] border border-teal-200" : "bg-slate-50 text-slate-400 border border-slate-200"}`}
                                    data-testid={`people-count-${u.user_id}`}
                                >
                                    {u.projects.length}
                                </span>
                            </td>
                            <td className="px-5 py-3 text-center text-sm tabular-nums text-slate-600">{u.leading || "—"}</td>
                            <td className="px-5 py-3">
                                {u.projects.length ? (
                                    <div className="flex flex-wrap gap-1.5">
                                        {u.projects.map(({ p, lead }) => (
                                            <button
                                                key={p.id}
                                                onClick={(e) => { e.stopPropagation(); onOpen(p); }}
                                                data-testid={`people-project-${u.user_id}-${p.id}`}
                                                className={`inline-flex items-center gap-1 h-6 px-2 rounded-md border text-xs font-medium transition-colors ${lead ? "bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100" : "bg-white border-slate-200 text-slate-700 hover:border-[#2EB4AC] hover:text-[#25908A]"}`}
                                            >
                                                {lead && <Crown className="h-3 w-3 text-amber-500" />}
                                                {p.name}
                                            </button>
                                        ))}
                                    </div>
                                ) : (
                                    <span className="text-xs text-slate-400 italic">Not on any project</span>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

// ---------- Kanban removed on user request ----------

// ---------- Avatar ----------
const Avatar = ({ user, size = "md", ring = "" }) => {
    const dim = size === "sm" ? "h-6 w-6 text-[10px]" : size === "lg" ? "h-10 w-10 text-sm" : "h-7 w-7 text-[11px]";
    if (!user) return <span className={`inline-flex rounded-full bg-slate-100 text-slate-500 items-center justify-center font-semibold ${dim} ${ring}`}>?</span>;
    if (user.picture) {
        return <img src={user.picture} alt={user.name} className={`rounded-full object-cover ${dim} ${ring}`} />;
    }
    return (
        <span
            className={`inline-flex rounded-full bg-teal-50 text-[#25908A] border border-teal-100 items-center justify-center font-semibold ${dim} ${ring}`}
            title={user.name}
        >
            {initialsOf(user.name)}
        </span>
    );
};

// ---------- Details Popup (glass / dark holo) ----------
// Read-only detail sheet any employee can open by clicking a project
// card. Glass panels on a deep-navy backdrop, teal glow accents that
// match the app theme (#2EB4AC). Edit button only surfaces for lead /
// member / creator / admin — everyone else sees "View only".
const ProjectDetailsPopup = ({ project: p, currentUser, onClose, onEdit }) => {
    React.useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose]);
    if (!p) return null;
    const s = statusMeta(p.status);
    const canEdit = canEditProject(p, currentUser);
    const created = p.created_at ? new Date(p.created_at) : null;
    const updated = p.updated_at ? new Date(p.updated_at) : null;
    const createdByUser = p.created_by_user;
    const fmt = (d) => d ? d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
    return createPortal(
        <div
            className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-md"
            onClick={onClose}
            data-testid="project-details-popup"
        >
            <div
                className="w-full max-w-3xl relative rounded-2xl overflow-hidden bg-gradient-to-br from-[#0B1220] via-[#0F1B2E] to-[#0B1220] ring-1 ring-white/10 shadow-[0_20px_80px_-20px_rgba(46,180,172,0.35)] max-h-[92vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
                style={{ backgroundImage: `radial-gradient(circle at 20% 0%, rgba(46,180,172,0.18), transparent 40%), radial-gradient(circle at 100% 100%, rgba(96,165,250,0.12), transparent 45%)` }}
            >
                {/* subtle grid overlay */}
                <div className="pointer-events-none absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)", backgroundSize: "32px 32px" }} />
                {/* teal glow ring */}
                <div className="pointer-events-none absolute -top-24 -left-16 h-64 w-64 rounded-full bg-[#2EB4AC]/25 blur-3xl" />
                <div className="pointer-events-none absolute -bottom-24 -right-16 h-72 w-72 rounded-full bg-fuchsia-500/10 blur-3xl" />

                {/* Header */}
                <div className="relative px-6 pt-5 pb-4 border-b border-white/10">
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1 flex items-start gap-4">
                            <ProjectIcon project={p} size={56} className="ring-1 ring-white/20" />
                            <div className="min-w-0">
                                <div className="text-[10px] uppercase tracking-[0.28em] text-[#2EB4AC] font-bold flex items-center gap-1.5">
                                    <span className="h-1.5 w-1.5 rounded-full bg-[#2EB4AC] animate-pulse" /> Project · {s.label}
                                </div>
                                <h2 className="mt-1.5 font-heading text-2xl sm:text-3xl font-bold text-white tracking-tight break-words">{p.name}</h2>
                                {p.description && <p className="mt-2 text-sm text-slate-300 max-w-2xl">{p.description}</p>}
                            </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                            {canEdit && (
                                <button
                                    onClick={onEdit}
                                    data-testid="project-details-edit-btn"
                                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-white/10 hover:bg-white/15 text-white text-xs font-semibold ring-1 ring-white/15 transition-colors backdrop-blur-sm"
                                >
                                    <Pencil className="h-3.5 w-3.5" /> Edit
                                </button>
                            )}
                            <button
                                onClick={onClose}
                                aria-label="Close"
                                data-testid="project-details-close"
                                className="h-9 w-9 inline-flex items-center justify-center rounded-lg bg-white/5 hover:bg-white/15 text-white/70 hover:text-white ring-1 ring-white/10 transition-colors"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Body */}
                <div className="relative flex-1 overflow-y-auto px-6 py-5 space-y-5">
                    {/* Lead + Team */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="col-span-1 rounded-xl bg-white/[0.04] ring-1 ring-white/10 p-4 backdrop-blur-sm">
                            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold flex items-center gap-1"><Crown className="h-3 w-3 text-amber-400" /> Lead</div>
                            <div className="mt-2 flex items-center gap-2">
                                <Avatar user={p.lead} ring="ring-2 ring-amber-400/40" size="lg" />
                                <div className="min-w-0">
                                    <div className="text-sm font-semibold text-white truncate">{p.lead?.name || "—"}</div>
                                    <div className="text-[11px] text-slate-400 truncate">{p.lead?.email || ""}</div>
                                </div>
                            </div>
                        </div>
                        <div className="col-span-1 sm:col-span-2 rounded-xl bg-white/[0.04] ring-1 ring-white/10 p-4 backdrop-blur-sm">
                            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold flex items-center gap-1"><Users className="h-3 w-3" /> Team · {(p.members || []).length}</div>
                            <div className="mt-2 flex flex-wrap gap-2">
                                {(p.members || []).map((m) => (
                                    <span
                                        key={m.user_id}
                                        title={`${m.name}${m.email ? " · " + m.email : ""}`}
                                        className="group inline-flex items-center gap-1.5 h-7 pl-1 pr-2.5 rounded-full bg-white/5 hover:bg-white/10 ring-1 ring-white/10 text-xs text-white transition-colors"
                                    >
                                        <Avatar user={m} size="sm" />
                                        <span className="truncate max-w-[140px]">{m.name}</span>
                                    </span>
                                ))}
                                {(p.members || []).length === 0 && <span className="text-xs text-slate-400 italic">No members yet</span>}
                            </div>
                        </div>
                    </div>

                    {/* Links */}
                    {((p.resources || []).length > 0 || (p.app_links || []).length > 0) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <LinkColumn title="Resources" items={p.resources} tone="slate" />
                            <LinkColumn title="App links" items={p.app_links} tone="indigo" />
                        </div>
                    )}

                    {/* Audit strip */}
                    <div className="rounded-xl bg-white/[0.03] ring-1 ring-white/10 px-4 py-3 grid grid-cols-1 sm:grid-cols-3 gap-3 text-[11px]">
                        <div>
                            <div className="uppercase tracking-wider text-slate-500 font-bold">Created by</div>
                            <div className="mt-1 flex items-center gap-1.5 text-slate-200">
                                {createdByUser ? <><Avatar user={createdByUser} size="sm" /><span className="truncate">{createdByUser.name}</span></> : <span className="text-slate-500">—</span>}
                            </div>
                        </div>
                        <div>
                            <div className="uppercase tracking-wider text-slate-500 font-bold">Created at</div>
                            <div className="mt-1 text-slate-200">{fmt(created)}</div>
                        </div>
                        <div>
                            <div className="uppercase tracking-wider text-slate-500 font-bold">Last updated</div>
                            <div className="mt-1 text-slate-200">{fmt(updated)}</div>
                        </div>
                    </div>

                    {!canEdit && (
                        <div className="text-[11px] text-slate-500 text-center italic">View only — ask the lead or a member to edit</div>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
};

const LinkColumn = ({ title, items, tone }) => {
    if (!items || items.length === 0) return null;
    const chipCls = tone === "indigo"
        ? "bg-indigo-500/10 text-indigo-200 border-indigo-400/30 hover:bg-indigo-500/20"
        : "bg-white/5 text-slate-100 border-white/15 hover:bg-white/10";
    return (
        <div className="rounded-xl bg-white/[0.04] ring-1 ring-white/10 p-4 backdrop-blur-sm">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold flex items-center gap-1.5">
                <LinkIcon className="h-3 w-3" /> {title} · {items.length}
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
                {items.map((l, i) => (
                    <a
                        key={i}
                        href={l.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={l.label || l.url}
                        className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-[11px] font-medium border transition-colors ${chipCls}`}
                    >
                        {l.label || _hostnameOf(l.url)}
                        <ExternalLink className="h-2.5 w-2.5 opacity-70" />
                    </a>
                ))}
            </div>
        </div>
    );
};

// ---------- Create / Edit dialog ----------
const ProjectDialog = ({ project, users, onClose, onSaved, onDeleted, currentUser }) => {
    const isEdit = !!project;
    const [name, setName] = useState(project?.name || "");
    const [description, setDescription] = useState(project?.description || "");
    const [leadId, setLeadId] = useState(project?.lead_id || currentUser?.user_id || "");
    const [memberIds, setMemberIds] = useState(new Set(project?.member_ids || (currentUser ? [currentUser.user_id] : [])));
    const [status, setStatus] = useState(project?.status || "active");
    const [iconUrl, setIconUrl] = useState(project?.icon_url || "");
    const [iconBusy, setIconBusy] = useState(false);
    const [resources, setResources] = useState(project?.resources || []);
    const [appLinks, setAppLinks] = useState(project?.app_links || []);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [memberSearch, setMemberSearch] = useState("");

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape" && !saving) onClose?.(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose, saving]);

    const activeUsers = useMemo(() => (users || []).filter((u) => u.active !== false).sort((a, b) => (a.name || "").localeCompare(b.name || "")), [users]);

    const memberCandidates = useMemo(() => {
        const s = memberSearch.trim().toLowerCase();
        return activeUsers.filter((u) => !s || (u.name || "").toLowerCase().includes(s) || (u.email || "").toLowerCase().includes(s));
    }, [activeUsers, memberSearch]);

    const toggleMember = (uid) => setMemberIds((prev) => {
        const next = new Set(prev);
        if (next.has(uid)) next.delete(uid); else next.add(uid);
        return next;
    });

    const submit = async () => {
        if (!name.trim()) return toast.error("Name is required");
        if (!description.trim()) return toast.error("Description is required");
        if (description.length > 200) return toast.error("Description must be 200 characters or fewer");
        if (!leadId) return toast.error("Pick a project lead");
        setSaving(true);
        try {
            const payload = {
                name: name.trim(),
                description: description.trim(),
                lead_id: leadId,
                member_ids: Array.from(memberIds),
                status,
                icon_url: iconUrl || "",
                resources: (resources || []).map((l) => ({ label: (l.label || "").trim(), url: (l.url || "").trim() })).filter((l) => l.url),
                app_links: (appLinks || []).map((l) => ({ label: (l.label || "").trim(), url: (l.url || "").trim() })).filter((l) => l.url),
            };
            if (isEdit) {
                await api.put(`/projects/${project.id}`, payload);
                toast.success("Project updated");
            } else {
                await api.post("/projects", payload);
                toast.success("Project created");
            }
            await onSaved?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        setDeleting(true);
        try {
            await api.delete(`/projects/${project.id}`);
            toast.success("Project deleted");
            await onDeleted?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        } finally {
            setDeleting(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} data-testid="project-dialog">
            <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl ring-1 ring-slate-900/5 overflow-hidden flex flex-col max-h-[92vh]" onClick={(e) => e.stopPropagation()}>
                <div className="px-6 pt-5 pb-3 border-b border-slate-100 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#1F7F79]">{isEdit ? "Edit project" : "New project"}</div>
                        <div className="mt-1 font-heading text-xl font-semibold text-slate-900 truncate">
                            {isEdit ? project.name : "Kick off an initiative"}
                        </div>
                    </div>
                    <button onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-900 -mr-2">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                    <div className="flex items-start gap-4">
                        <IconPicker
                            iconUrl={iconUrl}
                            busy={iconBusy}
                            onPick={async (file) => {
                                if (!file) return;
                                if (!file.type?.startsWith("image/")) {
                                    toast.error("Pick an image file (PNG, JPG, WebP)");
                                    return;
                                }
                                setIconBusy(true);
                                try {
                                    const dataUrl = await fileToSquareDataUrl(file, 256, 0.88);
                                    setIconUrl(dataUrl);
                                } catch {
                                    toast.error("Couldn't process that image");
                                } finally {
                                    setIconBusy(false);
                                }
                            }}
                            onClear={() => setIconUrl("")}
                        />
                        <div className="flex-1 min-w-0">
                            <Field label="Name">
                                <input
                                    autoFocus
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    data-testid="project-name-input"
                                    placeholder="e.g. Q1 Amazon listings refresh"
                                    className="w-full h-10 px-3 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#2EB4AC]/25 focus:border-[#2EB4AC] outline-none"
                                />
                            </Field>
                        </div>
                    </div>
                    <Field label="Description *" hint={`What outcome are you driving? Keep it under 200 characters. ${description.length}/200`}>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value.slice(0, 200))}
                            data-testid="project-desc-input"
                            rows={3}
                            maxLength={200}
                            required
                            className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-[#2EB4AC]/25 focus:border-[#2EB4AC] outline-none resize-none ${
                                description.trim().length === 0 ? "border-slate-200" : "border-slate-200"
                            }`}
                        />
                    </Field>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Lead">
                            <select
                                value={leadId}
                                onChange={(e) => setLeadId(e.target.value)}
                                data-testid="project-lead-select"
                                className="w-full h-10 px-3 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#2EB4AC]/25 focus:border-[#2EB4AC] outline-none bg-white"
                            >
                                <option value="">—</option>
                                {activeUsers.map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
                            </select>
                        </Field>
                        <Field label="Status">
                            <select
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                                data-testid="project-status-select"
                                className="w-full h-10 px-3 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#2EB4AC]/25 focus:border-[#2EB4AC] outline-none bg-white"
                            >
                                {STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                            </select>
                        </Field>
                    </div>

                    <Field label={`Members · ${memberIds.size}`} hint="The lead is auto-included. Add anyone who&apos;s contributing.">
                        <div className="border border-slate-200 rounded-lg overflow-hidden">
                            <div className="relative border-b border-slate-100">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                                <input
                                    value={memberSearch}
                                    onChange={(e) => setMemberSearch(e.target.value)}
                                    placeholder="Search…"
                                    className="w-full h-9 pl-9 pr-3 text-sm outline-none"
                                    data-testid="project-member-search"
                                />
                            </div>
                            <div className="max-h-48 overflow-y-auto">
                                {memberCandidates.map((u) => {
                                    const on = memberIds.has(u.user_id);
                                    return (
                                        <button
                                            key={u.user_id}
                                            type="button"
                                            onClick={() => toggleMember(u.user_id)}
                                            data-testid={`project-member-toggle-${u.user_id}`}
                                            className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors ${on ? "bg-teal-50" : "hover:bg-slate-50"}`}
                                        >
                                            <Avatar user={u} size="sm" />
                                            <div className="flex-1 min-w-0">
                                                <div className="font-medium text-slate-800 truncate">{u.name}</div>
                                                <div className="text-[11px] text-slate-500 truncate">{u.email}</div>
                                            </div>
                                            <span className={`h-5 w-5 inline-flex items-center justify-center rounded-md border ${on ? "bg-[#2EB4AC] border-[#2EB4AC] text-white" : "border-slate-300"}`}>
                                                {on && <Check className="h-3 w-3" />}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </Field>

                    <LinkListEditor
                        title="Resources"
                        hint="Docs, sheets, PDFs, briefs — anything read-only that supports the work."
                        placeholder="e.g. Product spec, Onboarding doc"
                        links={resources}
                        onChange={setResources}
                        testidPrefix="project-resource"
                    />

                    <LinkListEditor
                        title="App links"
                        hint="Jira boards, Notion pages, dashboards, repos — the operational surfaces you use daily."
                        placeholder="e.g. Amazon dashboard, Jira board"
                        links={appLinks}
                        onChange={setAppLinks}
                        testidPrefix="project-applink"
                    />
                </div>

                <div className="px-6 py-3 border-t border-slate-100 flex items-center justify-between gap-2 bg-slate-50/50">
                    <div>
                        {isEdit && canDeleteProject(project, currentUser) && (
                            confirmDelete ? (
                                <div className="inline-flex items-center gap-2">
                                    <span className="text-xs text-slate-600">Delete this project?</span>
                                    <button
                                        onClick={remove}
                                        disabled={deleting}
                                        data-testid="project-delete-confirm"
                                        className="inline-flex items-center gap-1 h-8 px-3 rounded-md text-xs font-semibold bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-50"
                                    >
                                        {deleting && <Loader2 className="h-3 w-3 animate-spin" />}
                                        Confirm
                                    </button>
                                    <button onClick={() => setConfirmDelete(false)} className="text-xs text-slate-500 hover:text-slate-700">Cancel</button>
                                </div>
                            ) : (
                                <button
                                    onClick={() => setConfirmDelete(true)}
                                    data-testid="project-delete-btn"
                                    className="inline-flex items-center gap-1 h-8 px-3 rounded-md text-xs font-semibold text-rose-600 hover:bg-rose-50"
                                >
                                    <Trash2 className="h-3 w-3" /> Delete
                                </button>
                            )
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={onClose} className="h-9 px-3 text-sm font-medium text-slate-600 hover:text-slate-900">Cancel</button>
                        <button
                            onClick={submit}
                            disabled={saving}
                            data-testid="project-save-btn"
                            className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-[#2EB4AC] hover:bg-[#25908A] text-white text-sm font-semibold disabled:opacity-50"
                        >
                            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                            <Save className="h-3.5 w-3.5" />
                            {isEdit ? "Save changes" : "Create project"}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
};

const Field = ({ label, hint, children }) => (
    <div>
        <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold block mb-1.5">{label}</label>
        {children}
        {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
    </div>
);

// LinkListEditor — editable list of {label, url} rows. Used twice in the
// project dialog: once for Resources, once for App links. Kept generic
// so both instances share the same styling and interaction pattern.
const LinkListEditor = ({ title, hint, placeholder, links, onChange, testidPrefix }) => {
    const set = (i, patch) => {
        const next = links.map((l, idx) => (idx === i ? { ...l, ...patch } : l));
        onChange(next);
    };
    const remove = (i) => onChange(links.filter((_, idx) => idx !== i));
    const add = () => onChange([...(links || []), { label: "", url: "" }]);
    return (
        <Field label={`${title}${links.length ? ` · ${links.length}` : ""}`} hint={hint}>
            <div className="space-y-2" data-testid={`${testidPrefix}-list`}>
                {(links || []).map((l, i) => (
                    <div key={i} className="flex items-center gap-2">
                        <input
                            value={l.label || ""}
                            onChange={(e) => set(i, { label: e.target.value })}
                            placeholder={placeholder}
                            data-testid={`${testidPrefix}-label-${i}`}
                            className="h-9 px-2.5 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#2EB4AC]/25 focus:border-[#2EB4AC] outline-none w-40 flex-shrink-0"
                        />
                        <div className="relative flex-1">
                            <LinkIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
                            <input
                                value={l.url || ""}
                                onChange={(e) => set(i, { url: e.target.value })}
                                placeholder="https://…"
                                data-testid={`${testidPrefix}-url-${i}`}
                                className="w-full h-9 pl-8 pr-2.5 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#2EB4AC]/25 focus:border-[#2EB4AC] outline-none"
                            />
                        </div>
                        <button
                            type="button"
                            onClick={() => remove(i)}
                            data-testid={`${testidPrefix}-remove-${i}`}
                            className="h-9 w-9 inline-flex items-center justify-center rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors flex-shrink-0"
                            aria-label="Remove"
                        >
                            <Minus className="h-4 w-4" />
                        </button>
                    </div>
                ))}
                <button
                    type="button"
                    onClick={add}
                    data-testid={`${testidPrefix}-add`}
                    className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-semibold text-[#25908A] hover:bg-teal-50 rounded-md transition-colors"
                >
                    <PlusIcon className="h-3.5 w-3.5" /> Add {title.toLowerCase()}
                </button>
            </div>
        </Field>
    );
};

// LinkStrip — compact chip row of the project's links, shown on the
// project card. Top-N surfaces the first few by list order (Resources
// before App links) so the poster is scannable; overflow becomes a
// "+N more" chip that opens the edit dialog for the full list.
const LinkStrip = ({ project, max = 4, onOverflow }) => {
    const all = [
        ...(project.resources || []).map((l) => ({ ...l, kind: "resource" })),
        ...(project.app_links || []).map((l) => ({ ...l, kind: "app" })),
    ];
    if (all.length === 0) return null;
    const shown = all.slice(0, max);
    const rest = all.length - shown.length;
    return (
        <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap gap-1.5" data-testid={`project-links-${project.id}`}>
            {shown.map((l, i) => (
                <a
                    key={i}
                    href={l.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={l.label || l.url}
                    data-testid={`project-link-${project.id}-${i}`}
                    onClick={(e) => e.stopPropagation()}
                    className={`inline-flex items-center gap-1 h-6 px-2 rounded-md text-[11px] font-medium border transition-colors ${
                        l.kind === "app"
                            ? "bg-indigo-50 text-indigo-800 border-indigo-200 hover:bg-indigo-100"
                            : "bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100"
                    }`}
                >
                    <LinkIcon className="h-3 w-3" />
                    <span className="max-w-[140px] truncate">{l.label || _hostnameOf(l.url)}</span>
                    <ExternalLink className="h-2.5 w-2.5 opacity-60" />
                </a>
            ))}
            {rest > 0 && (
                <button
                    type="button"
                    onClick={onOverflow}
                    data-testid={`project-links-more-${project.id}`}
                    className="inline-flex items-center h-6 px-2 rounded-md text-[11px] font-semibold bg-white text-slate-500 border border-dashed border-slate-300 hover:border-slate-400 hover:text-slate-700 transition-colors"
                >
                    +{rest} more
                </button>
            )}
        </div>
    );
};

const _hostnameOf = (url) => {
    try { return new URL(url).hostname.replace(/^www\./, ""); }
    catch { return url; }
};

export default Projects;
